import { type JSX, useMemo } from 'react';

import { createListCollection, EloButton, EloInputSearch, EloInputSelect } from '@plurall/elo-v3';
import { useNavigate } from '@tanstack/react-router';

import { CanAccess } from '@/components/CanAccess';
import {
  LEARNING_PATH_ORDERING_LABELS,
  LEARNING_PATH_STATUS_LABELS,
  learningPathOrderingSchema,
  studentLearningPathStatusSchema,
} from '@/features/learning-path';

import {
  LearningPathsToolbarFieldFactory,
  LearningPathsToolbarRootFactory,
  LearningPathsToolbarSearchFactory,
} from './learning-paths-toolbar.factory';
import { useEssaysContext } from '../../context/EssaysContext';

const ALL_STATUS = 'TODOS';

type SelectOption = { value: string; label: string };

type ToolbarSelectProps = {
  label: string;
  value: string;
  options: SelectOption[];
  /** Select width from md up; sized to fit the longest option without truncating. */
  width: string;
  onChange: (value: string) => void;
};

function ToolbarSelect({ label, value, options, width, onChange }: ToolbarSelectProps): JSX.Element {
  const collection = useMemo(() => createListCollection({ items: options }), [options]);

  return (
    <LearningPathsToolbarFieldFactory>
      <span>{label}</span>
      <EloInputSelect.Root
        collection={collection}
        value={[value]}
        variant='primary'
        width={{ base: '100%', md: width }}
        onValueChange={(details) => {
          const nextValue = details.value[0];
          if (nextValue && nextValue !== value) onChange(nextValue);
        }}>
        <EloInputSelect.ControlInput
          placeholder={label}
          flexProps={{ minW: 0, width: '100%' }}
          triggerProps={{ 'aria-label': label }}>
          <EloInputSelect.Indicator />
        </EloInputSelect.ControlInput>
        <EloInputSelect.Content scrollbarProps={{ maxHeight: 240 }}>
          {collection.items.map((item) => (
            <EloInputSelect.Item
              key={item.value}
              item={item}
              label={item.label}
            />
          ))}
        </EloInputSelect.Content>
      </EloInputSelect.Root>
    </LearningPathsToolbarFieldFactory>
  );
}

const ORDERING_OPTIONS: SelectOption[] = learningPathOrderingSchema.options.map((ordering) => ({
  value: ordering,
  label: LEARNING_PATH_ORDERING_LABELS[ordering],
}));

const EMPTY_SEARCH_COLLECTION = createListCollection<SelectOption>({ items: [] });

export function LearningPathsToolbar(): JSX.Element {
  const { isStudent, schoolId, filters, setFilters } = useEssaysContext();
  const navigate = useNavigate();

  const statusOptions = useMemo<SelectOption[]>(
    () => [
      { value: ALL_STATUS, label: 'Todos' },
      ...studentLearningPathStatusSchema.options
        .filter((status) => isStudent || status !== 'CONCLUIDA')
        .map((status) => ({ value: status, label: LEARNING_PATH_STATUS_LABELS[status] })),
    ],
    [isStudent]
  );

  return (
    <LearningPathsToolbarRootFactory>
      <LearningPathsToolbarSearchFactory>
        <EloInputSearch.Root
          collection={EMPTY_SEARCH_COLLECTION}
          variant='functional'
          width='100%'
          inputValue={filters.busca}
          onInputValueChange={({ inputValue, reason }) => {
            if (reason === 'interact-outside') return;
            setFilters({ busca: inputValue });
          }}>
          <EloInputSearch.Field placeholder='Busque por assunto, tema ou palavra-chave' />
        </EloInputSearch.Root>
      </LearningPathsToolbarSearchFactory>

      <ToolbarSelect
        label='Status'
        value={filters.status ?? ALL_STATUS}
        options={statusOptions}
        width='190px'
        onChange={(value) =>
          setFilters({ status: value === ALL_STATUS ? undefined : studentLearningPathStatusSchema.parse(value) })
        }
      />

      <ToolbarSelect
        label='Ordenar por'
        value={filters.ordenacao}
        options={ORDERING_OPTIONS}
        width='180px'
        onChange={(value) => setFilters({ ordenacao: learningPathOrderingSchema.parse(value) })}
      />

      {!isStudent && (
        <CanAccess policy='create:task'>
          <EloButton.Brand
            type='button'
            onClick={() => void navigate({ to: '/tarefa/nova', search: { escola: schoolId ?? undefined } })}>
            Criar tarefa
          </EloButton.Brand>
        </CanAccess>
      )}
    </LearningPathsToolbarRootFactory>
  );
}
